import React from 'react';
import PropTypes from 'prop-types';

import {
  Search as InventoryIcon,
} from '@material-ui/icons';

import { 
  ComponentGrouper,
  DetailPanel,
  FieldValue,
} from "../../../packages/detail-panel";

import {
  ResourceCard,
  ResourceRef,
} from '../../resource';

import { 
  fmtBigInteger,
  fmtBytes,
  fmtCurrency,
  fmtIso8601AsLocalDate,
  fmtIso8601AsLocalDateTime,
  fmtS3ConsoleLink,
} from '../../utils/formatters';

import { 
  BucketIcon,
} from '../../aws';

import { 
  fmtInventoryStatus,
  fmtObjectRetention,
} from '../formatters';

import {
  InventoryAction,
  InventoryResults
} from '.';

const handleGoToBucket = (bucket) => {
  window.open(fmtS3ConsoleLink(bucket),"_blank");
};

// const handleGoToInventory = () => {
//   window.open(fmtAthenaPreviewTableLink('dl_metadata','inventory'),"_blank");
// };

function isNumber(n) { return !isNaN(parseFloat(n)) && !isNaN(n - 0) }

const fmtObjectDateRange = (data) => {
  if(data?.OldestObjectTimestamp && data?.NewestObjectTimestamp) {
    const oldest=fmtIso8601AsLocalDate(data.OldestObjectTimestamp);
    const newest=fmtIso8601AsLocalDate(data.NewestObjectTimestamp);
    if(oldest===newest) {
      return newest;
    } else {
      return `${oldest} - ${newest}`;
    }
  }
  return "N/A";
};

const fmtObjectSizeRange = (data) => {
  if(isNumber(data?.MinObjectSize) && isNumber(data?.MaxObjectSize)) {
    const minSize=fmtBytes(data.MinObjectSize,2,false,true);
    const maxSize=fmtBytes(data.MaxObjectSize,2,false,true);
    if(minSize===maxSize) {
      return maxSize;
    } else {
      return `${minSize} - ${maxSize}`;
    }
  }
  return "N/A";
};

const fmtAdditionalCosts = (monthly) => {
  const parts=[];
  if(monthly?.Inventory) {
    if(monthly.Inventory>0.5) {
      parts.push(`${fmtCurrency(monthly.Inventory)} inventory`);
    }
  }
  if(monthly?.Monitoring) {
    if(monthly.Monitoring>0.5) {
      parts.push(`${fmtCurrency(monthly.Monitoring)} monitoring`);
    }
  }
  if(parts.length) {
    return parts.join(' / ');
  } else {
    return "~$0";
  }
};

export default function StorageResourceCard(props) {

  const { resource } = props;

  const [openInventory, setOpenInventory] = React.useState(false);

  if(!resource) return(null); 

  const actions=[];

  actions.push({ 
    name: `Go to Bucket...`,
    icon: BucketIcon,
    onClick: () => handleGoToBucket(resource.Bucket),
  });

  // if(resource.BucketConfiguration.InventoryStatus !== 'DISABLED') {
  //   actions.push({ 
  //     name: `Go to Inventory...`,
  //     icon: InventoryIcon,
  //     onClick: () => handleGoToInventory(),
  //   });
  // }
  
  if(resource.BucketConfiguration.InventoryStatus !== 'DISABLED') {
    actions.push({ 
      name: `Go to Inventory...`,
      icon: InventoryIcon,
      onClick: () => setOpenInventory(true),
    });
  }
  

  const fields=[
    { section: "Details", fields: [
      { name: "ResourceId", label: "ID", component: FieldValue, options: { maxLength: 40 } }, 
      { name: "ClientAccountId", label: "Client", component: ResourceRef, options: { type: 'Client' } },
      { name: "Bucket", label: "Bucket" }, 
      { name: "BucketConfiguration", label: "Inventory", formatter: fmtInventoryStatus },
      { name: "StackCreated", label: "Created", formatter: fmtIso8601AsLocalDateTime },
      { name: "StackUpdated", label: "Updated", formatter: fmtIso8601AsLocalDateTime },
    ]},
    { section: "Security", fields: [
      { name: "AuthorizedReadOnlyAccountIds", label: "Readers", component:ResourceRef, 
        options: { type: 'Client', format: 'csv', sorted: true }  },
      { name: "AuthorizedReadWriteAccountIds", label: "Writers", component:ResourceRef, 
        options: { type: 'Client', format: 'csv', sorted: true } },
      { name: "SecurityConfiguration.EncryptionMode", label: "Encryption" },
      { name: "BucketConfiguration.ObjectRetentionDays", label: "Retention", formatter: fmtObjectRetention },
    ]},    
  ];
  
  if (resource.Metrics) {
    fields.push(
      { section: "Metrics", fields: [
        { name: "Metrics.CloudWatch.Latest.Data.Bytes.Value", label: "Storage", formatter: fmtBytes },
        { name: "Metrics.CloudWatch.Latest.Data.Objects.Value", label: "Objects", formatter: fmtBigInteger },
        { name: "Metrics.CloudWatch.Latest.Data.BytesUploaded.Value", label: "Uploaded", formatter: fmtBytes },
        { name: "Metrics.CloudWatch.Latest.Data.BytesDownloaded.Value", label: "Downloaded", formatter: fmtBytes },
        { name: "Metrics.CloudWatch.Latest.Data.Requests.Value", label: "Requests", formatter: fmtBigInteger },
      ]});
  }

  if (resource?.Metrics?.Inventory?.Latest?.Data?.Cost) {
    fields.push(
      { section: "Inventory", fields: [
      { name: "Summary", label: "_", component: ComponentGrouper, options: { 
          components: [
            {
              component: InventoryResults,
              sm: 4,
              props: {
                value: resource,
                variant: 'graph',
              }
            },
            {
              component: DetailPanel,
              props: {
                data: resource.Metrics.Inventory.Latest,
                fields: [
                  { name: "Inventory Date", formatter: fmtIso8601AsLocalDate, data: "$.Timestamp" },
                  { name: "Date Range", formatter: fmtObjectDateRange, data: "$.Data" },
                  { name: "Size Range", formatter: fmtObjectSizeRange, data: "$.Data" },
                  { name: "Addt'l Costs", formatter: fmtAdditionalCosts, data: "$.Data.Cost.Estimated.Monthly" },
                ]
              }
            },
          ]}, data: "$" },
      { name: "InventoryTable", label: "_", component: InventoryResults, options: { variant: 'table' }, data: "$" },
    ]});
  }

  return (
    <React.Fragment>
      <ResourceCard resource={resource} fields={fields} actions={actions} stack dashboard />
      {openInventory?<InventoryAction resource={resource} onClose={() => setOpenInventory(false)}/>:null}
    </React.Fragment>
  );
}

StorageResourceCard.propTypes = {
  resource: PropTypes.object.isRequired,
};

StorageResourceCard.defaultProps = {
};
